import { mapProjectIdentity } from '../../utils/project';
import { buildOnboardingUrl } from '../../utils/studentPublicLinks';
import type {
  CheckoutFormState,
  CheckoutPaymentResult,
  CheckoutPixPaymentInstructions,
  CheckoutSelectedPlan,
} from './checkout.types';
import type { CheckoutSuccessSnapshotData, CheckoutSuccessTokenPayload } from './checkout.types';
import { CHECKOUT_SUCCESS_EXPIRY_MS } from './checkout.constants';
import {
  maskCardNumberForDisplay,
  isBrazilCheckoutCountry,
  resolveCheckoutPayerForSubmission,
  serializePlanBenefits,
  trimCheckoutFullName,
} from './checkout.utils';

export const CHECKOUT_SUCCESS_INVALID_URL_MESSAGE =
  'Esta URL não é válida ou expirou. Verifique o link recebido ou finalize a contratação novamente.';

export const buildCheckoutSuccessSnapshot = (
  form: CheckoutFormState,
  selectedPlan: CheckoutSelectedPlan,
  result: CheckoutPaymentResult,
  options?: { mode?: 'checkout' | 'charge_checkout' },
): CheckoutSuccessSnapshotData => ({
  personal: {
    ...form.personal,
    fullName: trimCheckoutFullName(form.personal.fullName),
  },
  address: { ...form.address },
  paymentMethod: form.paymentMethod!,
  card: form.paymentMethod === 'credit_card' && isBrazilCheckoutCountry(form.address.country)
    ? {
      holderName: form.card.holderName,
      number: maskCardNumberForDisplay(form.card.number),
      expiry: form.card.expiry,
    }
    : undefined,
  payer: form.paymentMethod === 'credit_card' && isBrazilCheckoutCountry(form.address.country)
    ? { ...resolveCheckoutPayerForSubmission(form) }
    : undefined,
  useStudentDataAsPayer: form.paymentMethod === 'credit_card' && isBrazilCheckoutCountry(form.address.country)
    ? form.useStudentDataAsPayer
    : undefined,
  selectedPlan: {
    ...selectedPlan,
    benefits: serializePlanBenefits(selectedPlan.benefits),
  },
  message: result.message,
  paymentInstructions: result.paymentInstructions ?? null,
  externalPaymentId: result.externalPaymentId
    ?? result.paymentInstructions?.externalPaymentId
    ?? null,
  discountCents: form.discountCents,
  mode: options?.mode ?? 'checkout',
  studentId: result.studentId && result.studentId > 0 ? result.studentId : null,
  onboardingToken: result.onboardingToken?.trim() || null,
});

export const buildCheckoutSuccessTokenPayload = (
  form: CheckoutFormState,
  selectedPlan: CheckoutSelectedPlan,
  result: CheckoutPaymentResult,
  options?: { mode?: 'checkout' | 'charge_checkout' },
): CheckoutSuccessTokenPayload => ({
  created_at: new Date().toISOString(),
  data: buildCheckoutSuccessSnapshot(form, selectedPlan, result, options),
});

export const isCheckoutSuccessTokenExpired = (createdAt: string): boolean => {
  const createdAtMs = Date.parse(createdAt);

  if (Number.isNaN(createdAtMs)) {
    return true;
  }

  return Date.now() - createdAtMs > CHECKOUT_SUCCESS_EXPIRY_MS;
};

export const parseCheckoutSuccessTokenPayload = (
  payload: Record<string, unknown>,
): CheckoutSuccessTokenPayload | null => {
  const createdAt = typeof payload.created_at === 'string' ? payload.created_at.trim() : '';
  const rawData = payload.data;

  if (!createdAt || !rawData || typeof rawData !== 'object' || Array.isArray(rawData)) {
    return null;
  }

  const data = rawData as Record<string, unknown>;
  const personal = data.personal;
  const address = data.address;
  const selectedPlan = data.selectedPlan;
  const paymentMethod = data.paymentMethod;

  if (
    !personal
    || typeof personal !== 'object'
    || Array.isArray(personal)
    || !address
    || typeof address !== 'object'
    || Array.isArray(address)
    || !selectedPlan
    || typeof selectedPlan !== 'object'
    || Array.isArray(selectedPlan)
    || typeof paymentMethod !== 'string'
    || typeof data.message !== 'string'
  ) {
    return null;
  }

  const parsedPlan = selectedPlan as CheckoutSelectedPlan;

  return {
    created_at: createdAt,
    data: {
      ...(data as unknown as CheckoutSuccessSnapshotData),
      selectedPlan: {
        ...parsedPlan,
        project: mapProjectIdentity(parsedPlan.project),
      },
    },
  };
};

export const resolveCheckoutOnboardingUrl = (token?: string | null): string | null => {
  const normalized = token?.trim();
  return normalized ? buildOnboardingUrl(normalized) : null;
};

export const buildAwaitingPaymentCheckoutResult = (
  snapshot: CheckoutSuccessSnapshotData,
  paymentInstructions: CheckoutPixPaymentInstructions,
  externalPaymentId: string,
): CheckoutPaymentResult => ({
  success: true,
  message: snapshot.message,
  paymentInstructions,
  externalPaymentId,
  studentId: snapshot.studentId && snapshot.studentId > 0 ? snapshot.studentId : null,
  onboardingToken: snapshot.onboardingToken?.trim() || null,
});

export const checkoutSuccessSnapshotToFormState = (
  snapshot: CheckoutSuccessSnapshotData,
): CheckoutFormState => ({
  personal: { ...snapshot.personal },
  address: { ...snapshot.address },
  paymentMethod: snapshot.paymentMethod,
  card: snapshot.card
    ? {
      holderName: snapshot.card.holderName,
      number: snapshot.card.number,
      expiry: snapshot.card.expiry,
      cvv: '',
    }
    : {
      holderName: '',
      number: '',
      expiry: '',
      cvv: '',
    },
  payer: snapshot.payer
    ? { ...snapshot.payer }
    : {
      name: '',
      email: '',
      cpf: '',
      postalCode: '',
      addressNumber: '',
      addressComplement: '',
      callingCode: '55',
      phone: '',
    },
  useStudentDataAsPayer: snapshot.useStudentDataAsPayer === true,
  discountCents: snapshot.discountCents ?? 0,
});
